import React, { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { EditorView } from "@codemirror/view";
import { Box, Button, Typography } from "@mui/material";
import { INDIGO, SLATE } from "../constants/Colors";

function isValidJson(val) {
  if (!val?.trim()) return true;
  try { JSON.parse(val); return true; } catch { return false; }
}

/**
 * JSON code editor with syntax highlighting, auto-indent, bracket matching,
 * a Format button, and a live valid/invalid indicator.
 *
 * Props:
 *   value      – string (raw JSON text)
 *   onChange   – called with updated string
 *   error      – boolean
 *   helperText – string shown below on error
 *   height     – CodeMirror editor height (default "200px")
 */
// Override the sky-blue active line and gutter highlights from the default light theme
const neutralTheme = EditorView.theme({
  ".cm-activeLine": { backgroundColor: "transparent !important" },
  ".cm-activeLineGutter": { backgroundColor: "transparent !important" },
  ".cm-gutters": {
    backgroundColor: "#ffffff",
    borderRight: `1px solid ${SLATE[200]}`,
    color: SLATE[300],
  },
  ".cm-content": { backgroundColor: "#ffffff" },
  ".cm-scroller": { backgroundColor: "#ffffff" },
  "&": { backgroundColor: "#ffffff" },
  ".cm-selectionBackground": { backgroundColor: `${INDIGO[100]} !important` },
  "&.cm-focused .cm-selectionBackground": { backgroundColor: `${INDIGO[100]} !important` },
  ".cm-cursor": { borderLeftColor: SLATE[700] },
});

export default function JsonEditor({ value, onChange, error, helperText, height = "200px" }) {
  const valid = isValidJson(value);
  const extensions = useMemo(() => [json(), neutralTheme], []);

  const handleFormat = () => {
    try { onChange(JSON.stringify(JSON.parse(value), null, 2)); } catch {}
  };

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${error ? "#d32f2f" : SLATE[300]}`,
          borderRadius: "8px",
          overflow: "hidden",
          transition: "border-color 0.15s",
          "&:focus-within": { borderColor: error ? "#d32f2f" : INDIGO[500] },
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "12px",
            py: "5px",
            bgcolor: SLATE[50],
            borderBottom: `1px solid ${SLATE[200]}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.05em" }}>
              JSON
            </Typography>
            {value?.trim() && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: valid ? "#22c55e" : "#ef4444", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.6875rem", fontWeight: 500, color: valid ? "#22c55e" : "#ef4444" }}>
                  {valid ? "Valid" : "Invalid"}
                </Typography>
              </Box>
            )}
          </Box>
          <Button
            size="small"
            onClick={handleFormat}
            disabled={!value?.trim() || !valid}
            sx={{
              minWidth: 0,
              px: "8px",
              py: "2px",
              fontSize: "0.6875rem",
              fontWeight: 600,
              textTransform: "none",
              color: SLATE[500],
              borderRadius: "4px",
              "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] },
              "&.Mui-disabled": { color: SLATE[300] },
            }}
          >
            Format
          </Button>
        </Box>

        <CodeMirror
          value={value || ""}
          onChange={onChange}
          extensions={extensions}
          height={height}
          theme="light"
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            indentOnInput: true,
            highlightActiveLine: true,
            highlightSelectionMatches: false,
            dropCursor: false,
            allowMultipleSelections: false,
          }}
          style={{ fontSize: "13px" }}
          placeholder={'{\n  "key": "value"\n}'}
        />
      </Box>

      {error && helperText && (
        <Typography sx={{ fontSize: "0.75rem", color: "#d32f2f", mt: "3px", ml: "14px" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
